import { getGetProjectQueryOptions, useGetProjectSuspense } from '@amarantos/api-client'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PageContent, PageHeader, PageSection, PageSectionContent, PageTitle } from '@/components/page'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { ProjectDocumentationForm } from '@/features/projects/components/project-documentation-form'

// The trailing underscore on `$projectId_` opts this page OUT of the `$projectId` layout (its tab
// bar and title). Settings are reached from the project's "…" menu, not from the tab row — a space
// of its own, so it carries a back button and its own breadcrumb tail instead. The URL is unchanged:
// `/projects/:id/settings`.
export const Route = createFileRoute('/_authenticated/projects/$projectId_/settings')({
	params: {
		parse: ({ projectId }) => ({ projectId: Number(projectId) }),
		stringify: ({ projectId }) => ({ projectId: String(projectId) }),
	},
	loader: ({ context: { queryClient }, params: { projectId } }) => {
		queryClient.ensureQueryData(getGetProjectQueryOptions(projectId))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_project_settings' />
	),
	component: ProjectSettingsRoute,
})

function ProjectSettingsRoute() {
	const { projectId } = Route.useParams()
	const { t } = useTranslation()
	const project = useGetProjectSuspense(projectId).data

	// Both the crumb and the back button aim at the project root, which forwards to the first tab of
	// whichever project-page iteration this project renders.
	const backToProject = { to: '/projects/$projectId', params: { projectId } } as const

	return (
		<>
			<PageHeader
				breadcrumbs={[
					{ label: t('nav.projects'), to: '/projects' },
					{ label: project.name, ...backToProject },
					{ label: t('projects.detail.settings.breadcrumb') },
				]}
			>
				<PageTitle
					subtitle={project.name}
					backButton={
						<Button asChild variant='ghost' size='icon-sm' aria-label={t('common.back')}>
							<Link {...backToProject}>
								<ChevronLeft />
							</Link>
						</Button>
					}
				>
					{t('projects.detail.settings.label')}
				</PageTitle>
			</PageHeader>
			<PageContent>
				<PageSection>
					<PageSectionContent>
						<ProjectDocumentationForm projectId={projectId} />
					</PageSectionContent>
				</PageSection>
			</PageContent>
		</>
	)
}
